interface StatCirclesItem {
  value: string;
  label: string;
  /** Override for `label` below `lg` when it differs from the design's
   *  wider-circle copy — e.g. "оплаты бонусами" vs "можно оплатить бонусами"
   *  (Figma node 5663:15416 uses the shorter phrasing for the narrow card). */
  mobileLabel?: string;
}

interface StatCirclesProps {
  title: string;
  /** Only rendered for the `cards` mobile variant — the `circles` variant
   *  (merchant landing) has no verified copy or mobile design for this yet. */
  description?: string;
  items: Array<StatCirclesItem>;
  className?: string;
  /** Below `lg`, render bordered cards instead of circles — the treatment the
   *  app landing's mobile design uses (Figma node 5663:15416): a 2×2 grid of
   *  179×95 cards with the value in accent violet above a grey label, both
   *  left-aligned, so the numbers stay readable at phone widths. The
   *  card→circle switch happens at `lg` rather than `sm`: turning these into
   *  circles as soon as the grid drops to 2 columns (at `sm`) makes each
   *  circle balloon to roughly half the row's width, well past its intended
   *  size — a rectangular card scales far more gracefully across the
   *  tablet range. Opt-in, because the merchant landing has no mobile design
   *  for this block yet and keeps circles at every width. */
  mobileVariant?: 'circles' | 'cards';
}

const ITEM_BASE = 'stat-ring bg-surface flex w-full flex-col';

const ITEM_CIRCLES =
  'mx-auto aspect-square items-center justify-center gap-1 rounded-full p-4 text-center sm:gap-3 sm:p-6';

// Fluid values scale linearly with viewport width between the two reference
// mockups — 402px (Figma node 5663:15416) and 1440px (5210:11228-adjacent,
// already verified against the design) — via the standard `clamp(min,
// intercept + slope*vw, max)` formula. These stay active across the *entire*
// range regardless of where the card→circle shape swap happens (`lg`), since
// padding/radius/gap all converge smoothly on the same 1440px target the
// shape-swap eventually reaches anyway — only the shape itself and the
// colours (which can't be interpolated via `calc()`) need a hard breakpoint.
const FLUID_OUTER_GAP = 'gap-[clamp(16px,6.71px+2.312vw,40px)]';
const FLUID_H2_SIZE = 'text-[clamp(20px,15.35px+1.156vw,32px)]';
const FLUID_H2_LEADING = 'leading-[clamp(28px,23.35px+1.156vw,40px)]';
const FLUID_DESCRIPTION_SIZE = 'text-[clamp(14px,13.23px+0.193vw,16px)]';
const FLUID_DESCRIPTION_LEADING = 'leading-[clamp(20px,18.45px+0.385vw,24px)]';
const FLUID_CARD_RADIUS = 'rounded-[clamp(16px,13.83px+0.578vw,24px)]';
const FLUID_CARD_PADDING = 'p-[clamp(16px,12.9px+0.771vw,24px)]';
const FLUID_VALUE_SIZE = 'text-[clamp(18px,11.03px+1.734vw,36px)]';
const FLUID_LABEL_SIZE = 'text-[clamp(14px,13.23px+0.193vw,16px)]';

const ITEM_CARDS =
  // No Tailwind `border` utility here — below `lg` the border is the
  // `.stat-ring-cards` spinning conic-gradient ring (styles.css); at `lg` it
  // falls back to the plain `.stat-ring` crossfade ring the circle variant
  // already uses. Either way it's a masked pseudo-element, never a real border.
  `stat-ring-cards min-h-[95px] gap-8 justify-between lg:mx-auto lg:aspect-square lg:min-h-0 lg:items-center lg:justify-center lg:gap-3 lg:rounded-full lg:text-center ${FLUID_CARD_RADIUS} ${FLUID_CARD_PADDING}`;

/** Presentational "stat" block: a heading over a row of circular stat items (the
 *  `.stat-ring` gradient ring lives in styles.css). Content is supplied by props
 *  so both landing pages can reuse it. */
export function StatCircles({
  title,
  description,
  items,
  className = '',
  mobileVariant = 'circles',
}: StatCirclesProps) {
  const cards = mobileVariant === 'cards';

  return (
    <div className={`flex flex-col ${cards ? FLUID_OUTER_GAP : 'gap-10'} ${className}`}>
      <div className="flex flex-col gap-2">
        <h2
          className={`text-ink font-semibold tracking-tight ${
            cards ? `${FLUID_H2_SIZE} ${FLUID_H2_LEADING}` : 'text-h2'
          }`}
        >
          {title}
        </h2>
        {cards && description ? (
          <p
            className={`text-text-secondary ${FLUID_DESCRIPTION_SIZE} ${FLUID_DESCRIPTION_LEADING}`}
          >
            {description}
          </p>
        ) : null}
      </div>
      <div className={`grid grid-cols-2 lg:grid-cols-4 ${cards ? 'gap-3 lg:gap-4' : 'gap-4'}`}>
        {items.map((stat) => (
          <div key={stat.label} className={`${ITEM_BASE} ${cards ? ITEM_CARDS : ITEM_CIRCLES}`}>
            <span
              className={
                cards
                  ? `lg:text-ink font-semibold tracking-tight text-[#5e4ac4] ${FLUID_VALUE_SIZE}`
                  : 'text-ink text-xl font-semibold tracking-tight sm:text-2xl md:text-3xl lg:text-4xl'
              }
            >
              {stat.value}
            </span>
            <span
              className={
                cards
                  ? `text-text-tertiary lg:text-ink ${FLUID_LABEL_SIZE}`
                  : 'text-ink text-xs sm:text-sm md:text-base'
              }
            >
              {cards && stat.mobileLabel ? (
                <>
                  <span className="lg:hidden">{stat.mobileLabel}</span>
                  <span className="hidden lg:inline">{stat.label}</span>
                </>
              ) : (
                stat.label
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
